import React, { useState } from 'react'
import {
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  ProgressBar
} from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const passwordStrength = (password: string) => {
  let score = 0
  if (password.length >= 8) score++
  if (/[A-Z]/.test(password)) score++
  if (/[0-9]/.test(password)) score++
  if (/[^A-Za-z0-9]/.test(password)) score++
  return score
}

/* ------------------------------------------------------------------ */
/* Validation                                                         */
/* ------------------------------------------------------------------ */

const SignupSchema = z
  .object({
    email: z.string().email('A valid email is required'),
    companyName: z.string().min(1, 'Company name is required'),
    userKind: z.string().refine(
      val => val === 'advertiser' || val === 'publisher',
      { message: 'Please select a valid user type' }
    ),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    passwordRepeat: z.string(),
    acceptTerms: z
      .boolean()
      .refine(val => val === true, {
        message: 'You must accept the terms and conditions'
      })
  })
  .refine(data => data.password === data.passwordRepeat, {
    path: ['passwordRepeat'],
    message: 'Passwords do not match'
  })

type SignupFormData = Omit<z.infer<typeof SignupSchema>, 'userKind'> & {
  userKind: string
}

const isDev = process.env.NODE_ENV === 'development'

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

const SignupForm: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setLoading] = useState(false)
  const [apiError, setApiError] = useState('')
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitted }
  } = useForm<SignupFormData>({
    resolver: zodResolver(SignupSchema),
    mode: 'onSubmit'
  })

  const password = watch('password') || ''
  const passwordRepeat = watch('passwordRepeat') || ''
  const strength = passwordStrength(password)
  const passwordsMatch = password.length > 0 && password === passwordRepeat

  const onSubmit = async (data: SignupFormData) => {
    try {
      setLoading(true)
      setApiError('')

      const origin = isDev
        ? 'http://localhost:3050/'
        : 'https://api.affdash.nordpar.com/'

      const res = await fetch(origin + 'signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })

      if (!res.ok) {
        const msg = await res.text()
        setApiError(msg)
        return
      }

      setSuccess(true)
    } catch {
      setApiError('Unable to create account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  /* ------------------------------------------------------------------ */
  /* Success view                                                       */
  /* ------------------------------------------------------------------ */

  if (success) {
    return (
      <div className="container d-flex justify-content-center mt-5">
        <Card className="col-sm-12 col-md-8 col-lg-6 text-center shadow-sm border-0">
          <Card.Body className="py-5 px-4">
            <h3 className="mb-3">Check your email 📬</h3>
            <p className="text-muted">
              We’ve sent you a confirmation email.
              <br />
              Click the link in the email to activate your account.
            </p>
            <Button className="mt-3" onClick={() => navigate('/login')}>
              Go to login
            </Button>
          </Card.Body>
        </Card>
      </div>
    )
  }

  /* ------------------------------------------------------------------ */
  /* Layout + Form                                                      */
  /* ------------------------------------------------------------------ */

  return (
    <div className="container-fluid min-vh-100">
      <div className="row min-vh-100">

        {/* Left – Form */}
        <div className="col-12 col-lg-6 d-flex align-items-center justify-content-center bg-light">
          <div className="w-100 px-3" style={{ maxWidth: 520 }}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4 p-md-5">

                <Card.Title className="text-center fw-semibold mb-1">
                  Start your 14-day free trial
                </Card.Title>
                <p className="text-center text-muted mb-4">
                  No credit card required
                </p>

                {apiError && <Alert variant="danger">{apiError}</Alert>}

                <Form onSubmit={handleSubmit(onSubmit)} noValidate>

                  {/* User kind */}
                  <Form.Group className="mb-3">
                    <Form.Label>Advertiser or Publisher</Form.Label>
                    <Form.Select
                      {...register('userKind')}
                      isInvalid={!!errors.userKind}
                      defaultValue=""
                    >
                      <option value="" disabled>Select user type</option>
                      <option value="advertiser">Advertiser</option>
                      <option value="publisher">Publisher</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.userKind?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Company */}
                  <Form.Group className="mb-3">
                    <Form.Label>Company name</Form.Label>
                    <Form.Control
                      {...register('companyName')}
                      isInvalid={!!errors.companyName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.companyName?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Email */}
                  <Form.Group className="mb-4">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      {...register('email')}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Password */}
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      {...register('password')}
                      isInvalid={!!errors.password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.password?.message}
                    </Form.Control.Feedback>

                    <ProgressBar
                      className="mt-2"
                      style={{ height: 6 }}
                      now={(strength / 4) * 100}
                      variant={
                        strength <= 1
                          ? 'danger'
                          : strength === 2
                          ? 'warning'
                          : 'success'
                      }
                    />
                  </Form.Group>

                  {/* Repeat password */}
                  <Form.Group className="mb-4">
                    <Form.Label>Repeat password</Form.Label>
                    <Form.Control
                      type="password"
                      {...register('passwordRepeat')}
                      isInvalid={!!errors.passwordRepeat}
                      isValid={passwordsMatch && isSubmitted}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.passwordRepeat?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Terms */}
                  <Form.Check
                    className="mb-4"
                    {...register('acceptTerms')}
                    label={
                <>
                  I agree to the{' '}
                  <a href="https://nordpar.com/terms-and-conditions/" target="_blank" rel="noreferrer">
                    Terms & Conditions
                  </a>
                  ,{' '}
                  <a href="https://nordpar.com/privacy-policy/" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="https://nordpar.com/data-processing-agreement/" target="_blank" rel="noreferrer">
                    Data Processing Agreement
                  </a>
                </>
              }
                    isInvalid={!!errors.acceptTerms}
                  />
                  {errors.acceptTerms && (
                    <div className="invalid-feedback d-block">
                      {errors.acceptTerms.message}
                    </div>
                  )}

                  <Button type="submit" className="w-100" disabled={isLoading}>
                    {isLoading ? 'Creating account…' : 'Create account'}
                  </Button>

                  {isLoading && (
                    <Spinner className="d-flex mt-3 mx-auto" />
                  )}

                </Form>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Right – Visual (desktop only) */}
<div className="d-none d-lg-flex col-lg-6 align-items-center justify-content-center signup-visual">
  <div className="text-white px-5" style={{ maxWidth: 520 }}>
    <h2 className="fw-bold mb-3">
      Get your affiliate dashboard up and running today
    </h2>
    <p className="fs-5 opacity-75 mb-4">
      Start with <strong>Nordpar</strong> and manage your campaigns, track performance, and grow revenue with a transparent, easy-to-use platform trusted by both advertisers and publishers.
    </p>
    <div className="d-flex flex-column flex-sm-row gap-2">
      <span className="badge bg-white text-dark p-3 rounded-pill shadow-sm mb-2 mb-sm-0">
        ✔ 14-Day Free Trial
      </span>
      <span className="badge bg-white text-dark p-3 rounded-pill shadow-sm mb-2 mb-sm-0">
        ✔ No developer required
      </span>
      <span className="badge bg-white text-dark p-3 rounded-pill shadow-sm">
        ✔ No credit card required
      </span>
    </div>
  </div>
</div>


      </div>
    </div>
  )
}

export default SignupForm
